import { Link } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { HelpCircle, BookOpen, MessageSquare, Settings } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from '@/components/ui/AnimatedCard';
import { useFeedbackStore } from '@/hooks/useFeedback';

const helpCategories = [
  {
    title: 'Getting Started',
    description: 'Learn the basics of using GodMode',
    icon: BookOpen,
    articles: [
      { title: 'Creating your first aspiration', slug: 'creating-your-first-aspiration' },
      { title: 'Setting up goals with SMART criteria', slug: 'setting-up-goals-with-smart-criteria' },
      { title: 'Organizing tasks with lists', slug: 'organizing-tasks-with-lists' },
      { title: 'Using the calendar', slug: 'using-the-calendar' }
    ]
  },
  {
    title: 'Features & Tips',
    description: 'Advanced features and productivity tips',
    icon: HelpCircle,
    articles: [
      { title: 'Creating and using list templates', slug: 'creating-and-using-list-templates' },
      { title: 'Setting up schedules', slug: 'setting-up-schedules' },
      { title: 'Notification settings', slug: 'notification-settings' },
      { title: 'Progress tracking and projections', slug: 'progress-tracking-and-projections' }
    ]
  },
  {
    title: 'Account & Settings',
    description: 'Manage your account and preferences',
    icon: Settings,
    articles: [
      { title: 'Configuring AI features', slug: 'configuring-ai-features' },
      { title: 'Backup and restore your data', slug: 'backup-and-restore-your-data' },
      { title: 'Managing your subscription', slug: 'managing-your-subscription' },
      { title: 'Sync and data storage', slug: 'sync-and-data-storage' }
    ]
  }
];

export function Help() {
  const { openFeedback } = useFeedbackStore();

  return (
    <Container size="lg" className="py-6">
      <Stack spacing="lg">

        <FadeIn>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Help Center
            </h1>
            <p className="text-muted-foreground">
              Find answers to common questions and learn how to get the most out of GodMode.
            </p>
          </div>
        </FadeIn>

        {/* Help Categories */}
        <StaggerContainer>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {helpCategories.map((category) => {
              const Icon = category.icon;
              return (
                <StaggerItem key={category.title}>
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Icon className="h-5 w-5 mr-2 text-primary" />
                        {category.title}
                      </CardTitle>
                      <CardDescription>
                        {category.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Stack spacing="sm">
                        {category.articles.map((article) => (
                          <Link
                            key={article.slug}
                            to={`/help/article/${article.slug}`}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors block py-1"
                          >
                            {article.title}
                          </Link>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </StaggerItem>
              );
            })}
          </div>
        </StaggerContainer>

        {/* Contact Support */}
        <Card>
          <CardHeader>
            <CardTitle>Need More Help?</CardTitle>
            <CardDescription>
              Can't find what you're looking for? Send us your feedback.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              className="justify-start"
              onClick={openFeedback}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Send Feedback
            </Button>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
